
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSidebarLogic } from "./hooks/useSidebarLogic";
import SidebarLogo from "./shared/SidebarLogo";
import SidebarNavigation from "./shared/SidebarNavigation";
import SidebarUser from "./shared/SidebarUser";

import TokenBalance from "../tokens/TokenBalance";
import TokenPurchaseModal from "../tokens/TokenPurchaseModal";

import { useTokens } from "../../hooks/useTokens";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  DrawerClose,
} from "@/components/ui/drawer";

const MobileSidebar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const { user, isActivePath, handleLogout, handleNewChat } = useSidebarLogic();
  const { tokenBalance } = useTokens();

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  const handleNewChatWithClose = async () => {
    await handleNewChat();
    setIsOpen(false);
  };

  const handleLogoutWithClose = async () => {
    await handleLogout();
    setIsOpen(false);
  };

  return (
    <>
      <div className="md:hidden">
        <Drawer open={isOpen} onOpenChange={setIsOpen}>
          <DrawerTrigger asChild>
            <Button variant="ghost" size="icon" className="h-10 w-10">
              <Menu className="h-6 w-6 text-foreground" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DrawerTrigger>
          <DrawerContent className="h-[80vh] bg-background">
            <DrawerHeader className="border-b border-border flex-shrink-0">
              <div className="flex items-center justify-between">
                <SidebarLogo onLinkClick={handleLinkClick} />
                <DrawerClose asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close menu</span>
                  </Button>
                </DrawerClose>
              </div>
            </DrawerHeader>

            <div className="flex flex-col h-full overflow-hidden">
              <div className="flex-1 overflow-y-auto p-4">
                {/* Navigation Links */}
                <div className="mb-6">
                  <SidebarNavigation 
                    isActivePath={isActivePath}
                    onLinkClick={handleLinkClick}
                    user={user}
                  />
                </div>

                <div className="flex-grow"></div>


                {/* Token Balance - Only show when logged in */}
                {user && (
                  <div className="mb-4">
                    <TokenBalance 
                      isCollapsed={false}
                      onPurchaseClick={() => setShowPurchaseModal(true)}
                    />
                  </div>
                )}

                {/* User profile section */}
                {user && (
                  <div className="border-t border-border pt-4 mb-4">
                    <SidebarUser 
                      user={user}
                      onLogout={handleLogoutWithClose}
                    />
                  </div>
                )}

                {/* Footer Links - Moved here for mobile */}
                <div className="border-t border-border pt-4">
                  <div className="flex flex-col space-y-3">
                    <Link 
                      to="/privacy-policy" 
                      onClick={handleLinkClick}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors py-1"
                    >
                      Privacy Policy
                    </Link>
                    <Link 
                      to="/terms-and-conditions" 
                      onClick={handleLinkClick}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors py-1"
                    >
                      Terms of Service
                    </Link>
                    <Link 
                      to="/faqs" 
                      onClick={handleLinkClick}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors py-1"
                    >
                      FAQs
                    </Link>
                    <a 
                      href="https://docs.google.com/forms/d/e/1FAIpQLSdKg7RG4snXV9mg-VAkjlwQQp4osIFcFlqmUep2OZQUbFEaXA/viewform?usp=dialog"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-muted-foreground hover:text-primary transition-colors py-1"
                    >
                      Submit Feedback
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      </div>

      {/* Token Purchase Modal */}
      <TokenPurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        currentBalance={tokenBalance}
      />
    </>
  );
};

export default MobileSidebar;
